import A from 'components/atoms/A/A';
import Button from 'components/atoms/Button/Button';
import { Checkbox } from 'components/atoms/Checkbox/Checkbox';
import ExternalLink from 'components/atoms/ExternalLink/ExternalLink';
import RegistrationCard from 'components/atoms/RegistrationCard/RegistrationCard';
import PasswordInput from 'components/atoms/TextInput/PasswordInput';
import UsernameInput, { FORM_KEY } from 'components/atoms/TextInput/UsernameInput';
import TurnstileWidget from 'components/atoms/TurnstileWidget/TurnstileWidget';
import Txt from 'components/atoms/Txt/Txt';
import Form from 'components/molecules/Form/Form';
import { useNamespace } from 'components/templates/Translation/TranslationWrapper';
import useFrontEndEventTrigger from 'hooks/useFrontEndEventTrigger';
import useUserSourceData from 'hooks/useUserSourceData';
import { useTranslation } from 'next-i18next';
import React from 'react';
import BackArrow from './assets/back-arrow.svg';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import RegistrationBenefits from './RegistrationBenefits';
import { StepProps } from './RegistrationForm';
import classes from './RegistrationForm.module.scss';

export const EMAIL_REQUIREMENT_DESCRIPTIONS = 'Please enter a valid email address';

export function UsernamePasswordStep({ onNext, currentFormData, error, onPrevious }: StepProps) {
  const [password, setPassword] = React.useState('');
  const isSocial = Boolean(currentFormData.provider || currentFormData.socialToken);
  const { frontEndEventTrigger } = useFrontEndEventTrigger();
  const namespace = useNamespace();
  const { t } = useTranslation(namespace);

  const { contactEmail } = useUserSourceData();
  const isJoinApi = Boolean(contactEmail && !currentFormData.socialToken);

  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null);
  const handleTurnstileTokenReceived = React.useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  function handleSubmit(formData: FormData) {
    onNext?.({
      username: formData.get(FORM_KEY)?.toString() ?? '',
      password: formData.get('password')?.toString() ?? '',
      terms: formData.get('privacyPolicy') === 'on',
      overrideCountry: formData.get('overrideCountry')?.toString(),
      turnstileToken: turnstileToken || undefined,
    });
    frontEndEventTrigger('Registration step', 'Initial email');
  }

  function handlePrevious(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    onPrevious?.();
  }

  return (
    <div className={classes.registrationFormContainer}>
      <RegistrationCard
        title={t('registrationGreeting', {
          firstName: currentFormData.firstName,
          defaultValue: "Hello {{firstName}},\nit's good to meet you",
        })}
        skipTitleTranslation
        additionalInfo={<RegistrationBenefits />}
      >
        {error}
        <Txt component="p" className={classes.formIntroFirst}>
          <strong>Now let&apos;s set up your account</strong>
        </Txt>
        <Txt component="p" className={classes.formIntroSecond}>
          To keep things secure, we&apos;ll text you a code to confirm.
        </Txt>
        <Form
          submitName="Sign Up"
          submitButtonType="primary"
          onSubmit={handleSubmit}
          submitDisabled={Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) && !turnstileToken}
          extraActionButtons={
            <>
              {!isJoinApi && (
                <Button
                  buttonSize="large"
                  buttonType="secondary"
                  color="primary"
                  className={classes.button}
                  onClick={handlePrevious}
                  iconStyle="iconOnly"
                  icon={<BackArrow className={classes.backIcon} />}
                >
                  <Txt>Back</Txt>
                </Button>
              )}
            </>
          }
        >
          <UsernameInput
            autoFocus={!isSocial}
            readOnly={isSocial}
            defaultValue={currentFormData.username}
          />

          <PasswordInput
            id="password"
            title="Password"
            placeholder="Enter password"
            required
            autoComplete="new-password"
            minLength={8}
            onInput={(e) => {
              setPassword(e.currentTarget.value);
            }}
            overrideErrorMessage={(_validity, _value) => {
              return undefined;
            }}
          />

          <PasswordStrengthIndicator currentLength={password.length} />

          {/* TODO: This is temporarily hardcoded for MobiWorkx MVP as the T&Cs were previously linking to Lifepoints from the back end call.
          It should be updated to be dynamic again when we update the gateway response */}
          <Checkbox
            labelElement={
              <>
                <Txt>To continue, please confirm that you have read and understood the</Txt>{' '}
                <A
                  href="https://mobiworkx.com/Account/PanelPrivacyPolicy?privacyPolicyID=13"
                  target="_blank"
                >
                  <Txt>Panel Privacy Policy</Txt>
                </A>
                ,{' '}
                <A
                  href="https://mobiworkx.com/Account/MemberShipTerms?membershipTermID=6"
                  target="_blank"
                >
                  <Txt>Membership Terms & Conditions</Txt>
                </A>
                {', '}
                <ExternalLink
                  link="refer-terms"
                  href="https://mobiworkx.com//Account/ReferAFriendTerms?referAFriendTermID=6"
                  target="_blank"
                >
                  <Txt>Refer a Friend Terms &amp; Conditions</Txt>
                </ExternalLink>{' '}
                <Txt>and</Txt>{' '}
                <A
                  href="https://mobiworkx.com/Account/RewardsProgramTerms?rewardsProgramTermID=1"
                  target="_blank"
                >
                  <Txt>Rewards Program Terms</Txt>
                </A>
              </>
            }
            name="privacyPolicy"
            required
            defaultChecked={error ? false : currentFormData.terms}
          />

          <TurnstileWidget onTokenReceived={handleTurnstileTokenReceived} action="register" />
        </Form>
      </RegistrationCard>
    </div>
  );
}
